import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
import { FORMSPREE_FORM_ID, contact, serviceOptions } from '../data/site';
const budgets = ['Prefer not to say', 'Under $500', '$500 – $1,500', '$1,500 – $5,000', '$5,000+'];
const timelines = ['ASAP', '1 – 2 weeks', '2 – 4 weeks', '1 – 3 months', 'Flexible'];
const sources = ['Instagram', 'Facebook', 'TikTok', 'Pinterest', 'Referral', 'Google', 'Other'];
const fieldClass = 'w-full border border-line bg-transparent px-4 py-3.5 text-sm text-ink placeholder:text-muted/70 focus:border-brand focus:outline-none';
const labelClass = 'label mb-2 block text-muted';
export function ContactForm() {
  const [state, handleSubmit] = useForm(FORMSPREE_FORM_ID);
  if (state.succeeded) {
    return <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }} className="border border-brand bg-brand/5 p-8 sm:p-12" role="status" aria-live="polite" data-id="element-142" data-mp="3phy25ijksnt6">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand" data-id="element-143" data-mp="3uj85kirmrqej">
          <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" data-id="element-144" data-mp="36n5dtduhxsw" />
        </span>
        <h3 className="mt-6 font-display text-3xl font-extrabold uppercase leading-none tracking-tightest sm:text-4xl" data-id="element-145" data-mp="84yg06byscc6">
          Request received.
        </h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-muted" data-id="element-146" data-mp="36onhnkqfopcb">
          Thank you — we’ve got your project details. We reply within 24 hours, every day. If it’s
          urgent, message us on WhatsApp and we’ll pick it up straight away.
        </p>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="grid gap-6" noValidate={false} data-id="element-147" data-mp="3kjqz3z4k18hi">
      {/* Routing metadata so submissions are identifiable in Formspree */}
      <input type="hidden" name="_subject" value="New project request — morvixgraphics.com" data-id="element-148" data-mp="zutjd6wnq2ju" />
      <input type="hidden" name="form_type" value="Project request" data-id="element-149" data-mp="2wvfudx95d2g1" />

      <div className="grid gap-6 sm:grid-cols-2" data-id="element-150" data-mp="2rxo1azwrnaa9">
        <div data-id="element-151" data-mp="2rww3rjjlz2hc">
          <label className={labelClass} htmlFor="name" data-id="element-152" data-mp="2rzidnptz1u2q">
            Full name *
          </label>
          <input id="name" name="name" type="text" required autoComplete="name" className={fieldClass} data-id="element-153" data-mp="35v2w6k5sga9m" />
          <ValidationError prefix="Name" field="name" errors={state.errors} className="mt-2 block text-xs text-brand" data-id="element-154" data-mp="30vzo0quihnxf" />
        </div>
        <div data-id="element-155" data-mp="297g4t0nkni31">
          <label className={labelClass} htmlFor="email" data-id="element-156" data-mp="297plfxmq8cw2">
            Email address *
          </label>
          <input id="email" name="email" type="email" required autoComplete="email" className={fieldClass} data-id="element-157" data-mp="2mzcsmsi7j5tm" />
          <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-2 block text-xs text-brand" data-id="element-158" data-mp="2i6t2yx68wk8g" />
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2" data-id="element-159" data-mp="1ufnrsvb92g4h">
        <div data-id="element-160" data-mp="1pi5opljwyv4e">
          <label className={labelClass} htmlFor="company" data-id="element-161" data-mp="1pie0tb87pwej">
            Company / brand
          </label>
          <input id="company" name="company" type="text" autoComplete="organization" className={fieldClass} data-id="element-162" data-mp="4zle00xq6puw" />
        </div>
        <div data-id="element-163" data-mp="9tfyh6l5n8v1">
          <label className={labelClass} htmlFor="service" data-id="element-164" data-mp="3s5uh9z04fhc8">
            Service needed *
          </label>
          <select id="service" name="service" required defaultValue="" className={fieldClass} data-id="element-165" data-mp="yrr9aw0rh3l">
            <option value="" disabled data-id="element-166" data-mp="op5bif8d4bnm">
              Select a service
            </option>
            {serviceOptions.map(service => <option key={service} value={service} data-id="element-167" data-mp="sp5w6hm21j34">
                {service}
              </option>)}
            <option value="Not sure yet" data-id="element-168" data-mp="eqg3de69q5kk">Not sure yet</option>
          </select>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2" data-id="element-169" data-mp="jrvnn40mg81w">
        <div data-id="element-170" data-mp="2zgnjx3cekxsk">
          <label className={labelClass} htmlFor="budget" data-id="element-171" data-mp="2zfvru2i0li71">
            Budget range (optional)
          </label>
          <select id="budget" name="budget" defaultValue="" className={fieldClass} data-id="element-172" data-mp="34hl2hh1brtul">
            <option value="" data-id="element-173" data-mp="3jqoo88418qcl">Select a range</option>
            {budgets.map(budget => <option key={budget} value={budget} data-id="element-174" data-mp="3exvltqw1du3o">
                {budget}
              </option>)}
          </select>
        </div>
        <div data-id="element-175" data-mp="3av8s1kft1z3u">
          <label className={labelClass} htmlFor="timeline" data-id="element-176" data-mp="35y8kt677yqb5">
            Project timeline
          </label>
          <select id="timeline" name="timeline" defaultValue="" className={fieldClass} data-id="element-177" data-mp="6iv49qufa3u7">
            <option value="" data-id="element-178" data-mp="2hjnmatj0sy5">Select a timeline</option>
            {timelines.map(timeline => <option key={timeline} value={timeline} data-id="element-179" data-mp="3tnuifp7y0xh1">
                {timeline}
              </option>)}
          </select>
        </div>
      </div>

      <div data-id="element-180" data-mp="3oo839a8k175l">
        <label className={labelClass} htmlFor="details" data-id="element-181" data-mp="3oohsgvlrzaa8">
          Project details *
        </label>
        <textarea id="details" name="details" rows={6} required placeholder="What are you building, who is it for, and what does success look like?" className={`${fieldClass} resize-y`} data-id="element-182" data-mp="2i6kyz2ote5fq" />
        <ValidationError prefix="Details" field="details" errors={state.errors} className="mt-2 block text-xs text-brand" data-id="element-183" data-mp="283xq5ppv1akg" />
      </div>

      <div data-id="element-184" data-mp="2d2ivu4nvsmga">
        <label className={labelClass} htmlFor="source" data-id="element-185" data-mp="2d1r1ex60surl">
          How did you hear about us?
        </label>
        <select id="source" name="source" defaultValue="" className={fieldClass} data-id="element-186" data-mp="2hwd8io52k15v">
          <option value="" data-id="element-187" data-mp="2ly8c5zah2qmt">Select an option</option>
          {sources.map(source => <option key={source} value={source} data-id="element-188" data-mp="2qtdwwus51z9b">
              {source}
            </option>)}
        </select>
      </div>

      <div className="flex flex-col gap-4 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-189" data-mp="30tekwu0yd3ju">
        <p className="text-xs text-muted" data-id="element-190" data-mp="11gu048qt74re">
          We reply within 24 hours. Prefer email?{' '}
          <a href={`mailto:${contact.email}`} className="text-brand underline underline-offset-4" data-id="element-191" data-mp="16eviergev01t">
            {contact.email}
          </a>
        </p>
        <button type="submit" disabled={state.submitting} className="bg-brand px-8 py-4 text-[0.78rem] font-medium uppercase tracking-[0.16em] text-white transition-colors hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60" data-cursor="hover" data-id="element-192" data-mp="ygs329rlm9kg">
          {state.submitting ? 'Sending…' : 'Send Project Request'}
        </button>
      </div>

      {state.errors ? <p className="text-sm text-brand" role="alert" data-id="element-193" data-mp="1ceqeb6amhpvq">
          We couldn’t send that just now. Please try again, or reach us directly by email or
          WhatsApp.
        </p> : null}
    </form>;
}